import React, { createContext, useState, useContext, useEffect, useRef } from "react";
import { TrendingUp, Users, DollarSign, Activity, BarChart2, PieChart, Calendar, ArrowUp, ArrowDown, Target, Zap, Globe, Database } from "lucide-react";

// Create the context
export const AnalyticsContext = createContext();

// Sample data from the original component
const overviewMetricsDefault = [
    {
        title: "Total Revenue (MRR)",
        value: "-",
        change: "-",
        positive: true,
        icon: DollarSign,
    },
    {
        title: "Total Users",
        value: "-",
        change: "-",
        positive: true,
        icon: Users,
    },
    {
        title: "Conversion Rate",
        value: "-",
        change: "-",
        positive: true,
        icon: TrendingUp,
    },
    // {
    //     title: "MQL to SQL Rate",
    //     value: "27.3%",
    //     change: "+5.2%",
    //     positive: true,
    //     icon: Target,
    // },
];

const conversionMetricsDefault = [
    {
        title: "Visitor to Signup",
        value: "-",
        change: "-",
        positive: true,
        target: "100",
        progress: 0,
    },
    {
        title: "Signup to Activation",
        value: "-",
        change: "-",
        positive: true,
        target: "100",
        progress: 0,
    },
    {
        title: "Activation to Paid",
        value: "-",
        change: "-",
        positive: true,
        target: "2",
        progress: 0,
    },
    {
        title: "Free to Paid (Overall)",
        value: "-",
        change: "-",
        positive: true,
        target: "2",
        progress: 0,
    },
];

const acquisitionChannels = [
    { channel: "Organic Search", visitors: "-", signups: "-", paid: "-", cac: "-" },
    { channel: "Direct", visitors: "-", signups: "-", paid: "-", cac: "-" },
    { channel: "LinkedIn", visitors: "-", signups: "-", paid: "-", cac: "-" },
    { channel: "Google Ads", visitors: "-", signups: "-", paid: "-", cac: "-" },
    { channel: "Referral", visitors: "-", signups: "-", paid: "-", cac: "-" },
    { channel: "Referral", visitors: "-", signups: "-", paid: "-", cac: "-" },
];

const geographicDataDefault = [
    { region: "United States", totalusers: "-", paidusers: "-", conversion: "-", arpu: "-" },
    { region: "Europe", totalusers: "-", paidusers: "-", conversion: "-", arpu: "-" },
    { region: "Asia", totalusers: "-", paidusers: "-", conversion: "-", arpu: "-" },
    { region: "Latin America", totalusers: "-", paidusers: "-", conversion: "-", arpu: "-" },
    { region: "Africa", totalusers: "-", paidusers: "-", conversion: "-", arpu: "-" },
    { region: "Russia", totalusers: "-", paidusers: "-", conversion: "-", arpu: "-" },
];

const userSegmentDataDefault = [
    { segment: "Leadership (C-Suite, VP, Director)", totalusers: "-", paidusers: "-", conversion: "-", arpu: "-", target: "-", gap: "-" },
    { segment: "Marketing", totalusers: "-", paidusers: "-", conversion: "-", arpu: "-", target: "-", gap: "-" },
    { segment: "Sales", totalusers: "-", paidusers: "-", conversion: "-", arpu: "-", target: "-", gap: "-" },
    { segment: "Students", totalusers: "-", paidusers: "-", conversion: "-", arpu: "-", target: "-", gap: "-" },
    { segment: "Other/Not Identified", totalusers: "-", paidusers: "-", conversion: "-", arpu: "-", target: "-", gap: "-" },
];

const monthlyTrendsDefault = [
    { month: "Jan", revenue: "-", users: "-", conversion: "-" },
    { month: "Feb", revenue: "-", users: "-", conversion: "-" },
    { month: "Mar", revenue: "-", users: "-", conversion: "-" },
    { month: "Apr", revenue: "-", users: "-", conversion: "-" },
    { month: "May", revenue: "-", users: "-", conversion: "-" },
];

const goalProgress = [
    { goal: "Reach 2% Conversion Rate", target: "-", current: "-", unit: "%" },
    { goal: "Increase MRR to $1M", target: "-", current: "-", unit: "$" },
    { goal: "Grow to 10M Users", target: "-", current: "-", unit: "" },
    { goal: "Enterprise Deals (20+)", target: "-", current: "-", unit: "" },
];

const experimentResults = [
    {
        name: "Home Page Redesign",
        metric: "Signup Rate",
        control: "-",
        variant: "-",
        improvement: "-",
        status: "-",
    },
    {
        name: "Onboarding Flow Optimization",
        metric: "Activation Rate",
        control: "-",
        variant: "-",
        improvement: "-",
        status: "-",
    },
    {
        name: "Pricing Page Update",
        metric: "Conversion Rate",
        control: "-",
        variant: "-",
        improvement: "-",
        status: "-",
    },
    {
        name: "Enterprise Feature Highlight",
        metric: "Demo Requests",
        control: "-",
        variant: "-",
        improvement: "-",
        status: "-",
    },
];

const activationMilestonesDefault = [
    {
        milestone: "Create First Presentation",
        completionRate: "-",
        change: "-",
        timeToComplete: "-",
        conversionImpact: "-",
        positive: true,
    },
    {
        milestone: "Connect Data Source",
        completionRate: "-",
        change: "-",
        timeToComplete: "-",
        conversionImpact: "-",
        positive: true,
    },
    {
        milestone: "Share Presentation",
        completionRate: "-",
        change: "-",
        timeToComplete: "-",
        conversionImpact: "-",
        positive: true,
    },
    {
        milestone: "Use Template",
        completionRate: "-",
        change: "-",
        timeToComplete: "-",
        conversionImpact: "-",
        positive: true,
    },
    {
        milestone: "Invite Team Member",
        completionRate: "-",
        change: "-",
        timeToComplete: "-",
        conversionImpact: "-",
        positive: true,
    },
];

// Create the context provider component
export const AnalyticsProvider = ({ children }) => {
    const [dateRange, setDateRange] = useState({
        preset: "yesterday",
        startDate: new Date(new Date().setDate(new Date().getDate() - 1)),
        endDate: new Date(new Date().setDate(new Date().getDate() - 1)),
        isCustom: false,
    });
    const [activeTab, setActiveTab] = useState("overview");
    const [revenueData, setRevenueData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [overviewMetrics, setOverviewMetrics] = useState(overviewMetricsDefault);
    const [monthlyTrends, setMonthlyTrends] = useState(monthlyTrendsDefault);
    const [geographicData, setGeographicData] = useState(geographicDataDefault);
    const [conversionMetrics, setConversionMetrics] = useState(conversionMetricsDefault);
    const [userSegmentData, setUserSegmentData] = useState(userSegmentDataDefault);
    const [activationMilestones, setActivationMilestones] = useState(activationMilestonesDefault);
    const userDataRef = useRef(null);
    // Context values to be shared
    const value = {
        dateRange,
        setDateRange,
        activeTab,
        setActiveTab,
        overviewMetrics,
        setOverviewMetrics,
        conversionMetrics,
        setConversionMetrics,
        acquisitionChannels,
        geographicData,
        setGeographicData,
        monthlyTrends,
        setMonthlyTrends,
        goalProgress,
        experimentResults,
        revenueData,
        setRevenueData,
        loading,
        setLoading,
        userData: userDataRef.current,
        userSegmentData,
        setUserSegmentData,
        activationMilestones,
        setActivationMilestones,
    };

    return <AnalyticsContext.Provider value={value}>{children}</AnalyticsContext.Provider>;
};

// Custom hook to use the context
export const useAnalytics = () => {
    const context = useContext(AnalyticsContext);
    if (context === undefined) {
        throw new Error("useAnalytics must be used within an AnalyticsProvider");
    }
    return context;
};
